import { atom } from 'infrastructure/state/jotai.wrapper'
import { fetchZipfile } from 'infrastructure/api'
import type { ResourceQuery } from 'infrastructure/api'
import { doAdminQueries } from '../providers/mutations'
import { atomWithRefresh } from 'infrastructure/state/atomWithRefresh'
import { Single } from 'infrastructure/api'

const queryKey = 'user'

type UserResponse = {
  id: number
  name: string
  email: string
  last_login?: string
  last_active?: string
  last_accessed_curriculum?: string
  platform_features: Single.PlatformFeature[]
  roles: string[]
  created_at: string
  classes: Single.UserClass[]
  school: Single.School
  copied_curricula: Single.CopiedCurricula[]
  deleted_at?: string
}

export async function fetchUser(id: number) {
  const resourceData: ResourceQuery = {
    key: queryKey,
    fields: [
      'id',
      'email',
      'name',
      'image_url',
      'roles',
      'created_at',
      'deleted_at',
      'last_login',
      'last_active',
      'last_accessed_curriculum',
      {
        school: ['id', 'name', { district: ['name'] }],
        classes: [
          'role',
          {
            class: ['id', 'name', 'alias', 'is_archived', 'class_voucher_code'],
          },
        ],
        platform_features: ['id', 'name', 'free_trial'],
        copied_curricula: [
          'id',
          'deleted_at',
          { original_curriculum: ['id', 'deleted', 'tree_type'] },
          { copied_curriculum: ['id', 'deleted', 'title'] },
        ],
      },
    ],
  }
  const params = { id }
  const response = await doAdminQueries<UserResponse>(
    [{ resourceData, params }],
    false,
  )
  return response.data?.[queryKey]
}

export const userIdAtom = atom<number | undefined>(undefined)

export const userResponseAtom = atomWithRefresh((get) => {
  const userId = get(userIdAtom)
  if (!userId) {
    return
  }
  return fetchUser(userId)
})

export function createUserSearchParams(filters: Record<string, string>) {
  const req = new URLSearchParams()
  if ('district' in filters) {
    if (Number(filters.district)) {
      req.set('districtID', filters.district)
    } else {
      req.set('districtName', filters.district)
    }
  }
  if ('email' in filters) {
    req.set('email', filters.email)
  }
  if ('role' in filters) {
    req.set('role', filters.role)
  }
  if ('school' in filters) {
    if (Number(filters.school)) {
      req.set('schoolID', filters.school)
    } else {
      req.set('schoolName', filters.school)
    }
  }
  // replace * with something else for SQL
  if ('user' in filters) {
    if (Number(filters.user)) {
      req.set('userID', filters.user)
    } else {
      req.set('nameOfUser', filters.user)
    }
  }
  if ('includeSoftDeleted' in filters) {
    req.set('includeSoftDeleted', filters.includeSoftDeleted)
  }
  if ('onlyDeleted' in filters) {
    req.set('onlyDeleted', filters.onlyDeleted)
  }
  if ('includeDemoUsers' in filters) {
    req.set('includeDemoUsers', filters.includeDemoUsers)
  }
  return req
}

// fetchUserCSV gets the user csv zip file
export async function fetchUserCSV(filters: Record<string, string>) {
  const req = createUserSearchParams(filters)
  await fetchZipfile(`/v1/kat/users-export?${req.toString()}`)
}
